<script setup lang="ts">
  import { computed, nextTick, onMounted, useTemplateRef, watch } from 'vue'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    FlagIcon,
    Modal,
    ModalContent,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    useAppLoading
  } from '@gat-ship/vue-components'
  import { IconCurrency, IconLoading } from '@gat-ship/icons'
  import { useCurrenciesProvider } from '../providers/CurrenciesProvider.vue'
  import { useCurrencyUtils } from '../../composables/useCurrencyUtils'
  import { testIds, type Currency } from '@gat-ship/types'

  const currenciesProvider = useCurrenciesProvider()

  const { selectVisibleCurrencies } = useCurrencyUtils()

  const loading = useAppLoading()

  onMounted(async () => {
    await currenciesProvider.fetchCurrencies()
    await currenciesProvider.fetchAllPlatformCurrencies()
  })

  const isOpen = defineModel<boolean>('open', { default: false })

  /**
   * Every currency eligible to be added. `Command` owns the text filtering
   * (its `useFilter` narrows the visible items as the user types), so we pass
   * no search term here — this is just the non-historical, not-yet-added set.
   */
  const eligibleCurrencies = computed(() =>
    selectVisibleCurrencies(
      currenciesProvider.allPlatformCurrencies.value,
      currenciesProvider.currencies.value ?? [],
      undefined
    )
  )

  function loadingId(code: string) {
    return 'currency-' + code
  }

  async function onAddCurrency(currency: Currency) {
    if (loading.isLoading(loadingId(currency.code))) {
      return
    }
    await currenciesProvider.addCurrency(currency)
  }

  // `Command` forwards the inner ListboxRoot expose (`highlightFirstItem`).
  const commandRef = useTemplateRef<{ highlightFirstItem?: () => void }>(
    'commandRef'
  )

  // reka-ui only re-highlights on input events; async list changes (currencies
  // loading, an added currency dropping out) need an explicit refresh so Enter
  // always has a target. Same pattern as global-search.
  watch([eligibleCurrencies, isOpen], async () => {
    if (!isOpen.value) {
      return
    }
    await nextTick()
    commandRef.value?.highlightFirstItem?.()
  })

  export type ShowAddCurrencyDialogParams = Record<string, never>

  function showModal(_params?: ShowAddCurrencyDialogParams) {
    isOpen.value = true
  }
  defineExpose({
    showModal
  })
</script>

<template>
  <modal v-model:open="isOpen" type="drawer" close-on-outside-click>
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconCurrency class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Add Currency
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        Select a currency to add it to your system.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <Command ref="commandRef" class="bg-transparent">
        <CommandInput placeholder="Search currencies by name or code" />
        <CommandList class="max-h-[calc(100vh-200px)] min-h-[100px]">
          <CommandEmpty>No currencies found.</CommandEmpty>
          <CommandGroup class="p-0">
            <CommandItem
              v-for="currency in eligibleCurrencies"
              :key="currency.code"
              :value="currency.code"
              :disabled="loading.isLoading(loadingId(currency.code))"
              @select="onAddCurrency(currency)"
            >
              <div class="text-muted-foreground size-4">
                <IconLoading
                  v-if="loading.isLoading(loadingId(currency.code))"
                />
                <FlagIcon
                  v-else
                  class="rounded-full"
                  :flag="currency.flagCode"
                />
              </div>
              <span
                class="font-medium"
                :data-testid="
                  testIds.currency.action.addCurrencyToSystem(currency.code)
                "
              >
                {{ currency.code }}
              </span>
              <span class="text-muted-foreground">- {{ currency.name }}</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </ModalContent>
  </modal>
</template>
